/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useCallback, useState, useMemo, useRef, createContext } from 'react'
import BigNumber from 'bignumber.js'
import { Text, Flex, Button, ButtonMenu, ToggleMe, useMatchBreakpointsContext, Toggle } from '@pancakeswap/uikit'
import styled from 'styled-components'
import { ViewMode } from 'state/user/actions'
import Page from 'views/Page/Page'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import { DeserializedFarm } from 'state/types'
import orderBy from 'lodash/orderBy'
import { latinise } from 'utils/latinise'
import { useRouter } from 'next/router'
import Select, { OptionProps } from 'components/Select/Select'
import { FarmWithStakedValue } from './components/types'
import { useNftPoolsFarms } from 'state/xFarms/hooks'
import { useUserFarmsViewMode } from 'state/user/hooks'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { useTranslation } from '@pancakeswap/localization'
import { priceDexScreener } from 'utils/tokenPricing'
import { BIG_ZERO } from 'utils/bigNumber'
import { getTokenAddress } from 'config/constants/token-info'
import { useWeb3React } from '@web3-react/core'
import RiseLoader from 'react-spinners/RiseLoader'

import {
  FarmsHeader,
  ViewAndSortContainer,
  LabelWrapper,
  FilterContainer,
  StakedOnlyWrapper,
  ViewAndStaked,
  ToggleHousing,
  ToggleText,
} from './styled'

import dynamic from 'next/dynamic'
import { useGetNftPoolsQuery } from 'graphql/data/generated/graphql-codegen-generated'
import useNFTPools from 'hooks/useNFTPools'
const PoolV3 = dynamic(() => import('views/PoolV3'), { ssr: true })
const FlexLayout = dynamic(() => import('components/Layout/Flex'), { ssr: true })

// NEW FARMS VIEW
const Tabs = styled.div`
  max-width: 100vw !important;
  padding: 0px 0px 24px 0px;
`

export enum FarmType {
  STANDARD,
  MANUAL,
  AUTOMATIC,
}

const NUMBER_OF_FARMS_VISIBLE = 18

const Farms: React.FC = ({ children }) => {
  const [numberOfFarmsVisible, setNumberOfFarmsVisible] = useState(NUMBER_OF_FARMS_VISIBLE)
  const { query: urlQuery } = useRouter()
  const { account } = useWeb3React()
  const { arxPerSec, WETHPerSec, farms: farmsLP } = useNftPoolsFarms()
  const { t } = useTranslation()
  const { isMobile, isDesktop } = useMatchBreakpointsContext()

  const [_query, setQuery] = useState('')
  const normalizedUrlSearch = useMemo(() => (typeof urlQuery?.search === 'string' ? urlQuery.search : ''), [urlQuery])
  const query = normalizedUrlSearch && !_query ? normalizedUrlSearch : _query
  const [viewMode, setViewMode] = useUserFarmsViewMode()
  const [sortOption, setSortOption] = useState('hot')
  const { observerRef, isIntersecting } = useIntersectionObserver()
  const chosenFarmsLength = useRef(0)

  const sortedFarmsLP = farmsLP
  const [stakedOnlyState, setStakedOnlyState] = useState(localStorage?.getItem('stakedOnlyFarms') === 'true')
  const setStakedOnly = () => {
    const stakedOnly = localStorage?.getItem('stakedOnlyFarms') === 'true'
    if (stakedOnly) {
      localStorage.setItem('stakedOnlyFarms', 'false')
      setStakedOnlyState(false)
    } else {
      localStorage.setItem('stakedOnlyFarms', 'true')
      setStakedOnlyState(true)
    }
  }

  // New Logic for Sorting
  const INACTIVE_PIDS = [9, 2, 15, 18, 17, 12, 13, 9001, 9002, 9003, 9004]
  const isConcentrated = (pid) => pid > 9000
  const isInactive = (pid) => INACTIVE_PIDS.includes(pid)
  const isActive = (pid) => !isInactive(pid) && !isConcentrated(pid)
  const [activeFarms, setActiveFarms] = useState([])
  const [inactiveFarms, setInactiveFarms] = useState([])
  const [concentratedFarms, setConcentratedFarms] = useState([])
  const [showInactive, setShowInactive] = useState(false)
  const [showConcentrated, setShowConcentrated] = useState(false)

  useEffect(() => {
    const newActiveFarms = []
    const newInactiveFarms = []
    const newConcentratedFarms = []

    sortedFarmsLP.forEach((farm) => {
      if (isInactive(farm.pid)) {
        newInactiveFarms.push(farm)
      } else if (isConcentrated(farm.pid)) {
        newConcentratedFarms.push(farm)
      } else {
        newActiveFarms.push(farm)
      }
    })

    setActiveFarms(newActiveFarms)
    setInactiveFarms(newInactiveFarms)
    setConcentratedFarms(newConcentratedFarms)
  }, [sortedFarmsLP])

  const farmsList = useCallback(
    (farmsToDisplay: DeserializedFarm[]): FarmWithStakedValue[] => {
      let farmsToDisplayWithAPR: FarmWithStakedValue[] = farmsToDisplay.map((farm) => {
        if (!farm.lpTotalInQuoteToken || !farm.quoteTokenPriceBusd) {
          return farm
        }

        return { ...farm, apr: isActive ? farm.apr : 0, liquidity: new BigNumber(farm.TVL || 1) }
      })
      if (query) {
        const lowercaseQuery = latinise(query.toLowerCase())
        farmsToDisplayWithAPR = farmsToDisplayWithAPR.filter((farm: FarmWithStakedValue) => {
          return latinise(farm.lpSymbol.toLowerCase()).includes(lowercaseQuery)
        })
      }
      return farmsToDisplayWithAPR
    },
    [query, isActive, arxPerSec, WETHPerSec, farmsLP],
  )

  useEffect(() => {
    if (isIntersecting) {
      setNumberOfFarmsVisible((farmsCurrentlyVisible) => {
        if (farmsCurrentlyVisible <= chosenFarmsLength.current) {
          return farmsCurrentlyVisible + NUMBER_OF_FARMS_VISIBLE
        }
        return farmsCurrentlyVisible
      })
    }
  }, [isIntersecting])

  const handleSortOptionChange = (option: OptionProps): void => {
    setSortOption(option.value)
  }

  const chosenFarmsMemoized = useMemo(() => {

    let chosenFarms = []
    const sortFarms = (farms: FarmWithStakedValue[]): FarmWithStakedValue[] => {
      switch (sortOption) {
        case 'apr':
          return orderBy(farms, (farm: FarmWithStakedValue) => farm.swapFeeAPR + farm.apr, 'desc');
          case 'multiplier':
          return orderBy(
            farms,
            (farm: FarmWithStakedValue) => (farm.multiplier ? Number(farm.multiplier.slice(0, -1)) : 0),
            'desc',
          )
        case 'earned':
          return orderBy(
            farms,
            (farm: FarmWithStakedValue) => (farm.userData ? Number(farm.userData.earnings) : 0),
            'desc',
          )
        case 'liquidity':
          return orderBy(farms, (farm: FarmWithStakedValue) => Number(farm.liquidity), 'desc')
        case 'latest':
          return orderBy(farms, (farm: FarmWithStakedValue) => Number(farm.pid), 'desc')
        default:
          return farms
      }
    }
    if (showConcentrated) {
      chosenFarms = farmsList(concentratedFarms)
    } else if (showInactive) {
      chosenFarms = farmsList(inactiveFarms)
    } else {
      chosenFarms = farmsList(activeFarms)
    }
    return sortFarms(chosenFarms).slice(0, numberOfFarmsVisible)
  }, [
    sortOption,
    activeFarms,
    farmsList,
    inactiveFarms,
    concentratedFarms,
    showConcentrated,
    isActive,
    isInactive,
    numberOfFarmsVisible,
    farmsLP,
  ])
  chosenFarmsLength.current = chosenFarmsMemoized.length

  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('farmView')
    return savedView ? JSON.parse(savedView) : FarmType.STANDARD
  })

  useEffect(() => {
    localStorage.setItem('farmView', JSON.stringify(view))
    if (view === FarmType.AUTOMATIC) {
      setShowConcentrated(false)
    } else {
      setShowConcentrated(false)
    }
  }, [view])

  const handleClick = (newIndex: number) => {
    setView(newIndex)
  }

  const TabsComponent: React.FC = () => (
    <Tabs style={{ width: '99%' }}>
      <ButtonMenu scale="swaptoggle" onItemClick={handleClick} activeIndex={view} fullWidth>
        <ToggleMe style={{ height: 38 }}>
          <ToggleText>{t('Standard')} </ToggleText>
        </ToggleMe>
        <ToggleMe style={{ height: 38 }}>
          <ToggleText>{t('Concentrated')} </ToggleText>
        </ToggleMe>
      </ButtonMenu>
    </Tabs>
  )

  return (
    <FarmsContext.Provider value={{ chosenFarmsMemoized, viewMode, stakedOnlyState }}>
      <Page
        backgroundImageSrc="/images/vikings/clean/1.webp"
        backgroundDefaultOpacity={1}
        backgroundDesktopOpacity={0.3}
        // @ts-ignore
        style={{ position: 'relative' }}
      >
        <FarmsHeader>Farms</FarmsHeader>

        <ToggleHousing>
          <TabsComponent />
        </ToggleHousing>

        {!account ? (
          <ConnectWalletButton />
        ) : (
          <>
            {view === FarmType.STANDARD && (
              <>
                <ViewAndSortContainer>
                  <ViewAndStaked>
                    {/* <ToggleView idPrefix="clickFarm" viewMode={viewMode} onToggle={setViewMode} /> */}
                    <StakedOnlyWrapper>
                      <Toggle
                        id="staked-only-farms"
                        checked={stakedOnlyState}
                        onChange={() => setStakedOnly()}
                        scale="sm"
                      />
                      <Text fontSize="14px"> {t('STAKED ONLY')}</Text>
                    </StakedOnlyWrapper>
                  </ViewAndStaked>
                  <FilterContainer>
                    <LabelWrapper>
                      {/* <Text textTransform="uppercase">{t('Sort by')}</Text> */}
                      <Select
                        options={[
                          { label: t('Hot'), value: 'hot' },
                          { label: t('TVL'), value: 'liquidity' },

                          { label: t('APR'), value: 'apr' },
                          { label: t('Latest'), value: 'latest' },
                        ]}
                        onOptionChange={handleSortOptionChange}
                      />
                    </LabelWrapper>
                  </FilterContainer>
                </ViewAndSortContainer>

                <FlexLayout style={{ paddingBottom: '100px' }}>{children}</FlexLayout>
                <Flex
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  marginTop="1rem"
                  marginBottom={isMobile ? '1rem' : '0rem'}
                >
                  <Button onClick={() => setShowInactive((prev) => !prev)}>
                    {showInactive ? 'Show Active Farms' : 'Show Inactive Farms'}
                  </Button>
                </Flex>
              </>
            )}

            {view === FarmType.AUTOMATIC && (
              <>
                <Flex
                  flex="1"
                  padding="12px"
                  flexDirection="column"
                  justifyContent="center"
                  style={{
                    backdropFilter: 'blur(12px)',
                    borderRadius: '12px',
                  }}
                >
                  <Text
                    fontWeight="600"
                    color="secondary"
                    textAlign={['center', null, null, 'center']}
                    fontSize={isDesktop ? '16px' : '16px'}
                  >
                    BaseSwap no longer supports the usage of DefiEdge concentrated liquidity. Please withdraw your
                    deposits.
                  </Text>
                </Flex>
                <ViewAndSortContainer>
                  <ViewAndStaked>
                    {/* <ToggleView idPrefix="clickFarm" viewMode={viewMode} onToggle={setViewMode} /> */}
                    <StakedOnlyWrapper>
                      <Toggle
                        id="staked-only-farms"
                        checked={stakedOnlyState}
                        onChange={() => setStakedOnly()}
                        scale="sm"
                      />
                      <Text> {t('Staked only')}</Text>
                    </StakedOnlyWrapper>
                  </ViewAndStaked>
                  <FilterContainer>
                    <LabelWrapper>
                      <Text textTransform="uppercase">{t('Sort by')}</Text>
                      <Select
                        options={[
                          { label: t('Hot'), value: 'hot' },
                          { label: t('APR'), value: 'apr' },
                          { label: t('Liquidity'), value: 'liquidity' },
                          { label: t('Latest'), value: 'latest' },
                        ]}
                        onOptionChange={handleSortOptionChange}
                      />
                    </LabelWrapper>
                  </FilterContainer>
                </ViewAndSortContainer>

                <FlexLayout>{children}</FlexLayout>
              </>
            )}

            {view === FarmType.MANUAL && <PoolV3 />}

            {account && !farmsLP.length && (
              <Flex alignItems="flex-start" justifyContent="flex-start" marginTop="4rem">
                <RiseLoader
                  color="#37C0DF"
                  size={8}
                  speedMultiplier={1.2}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />{' '}
              </Flex>
            )}

            <div ref={observerRef} />
          </>
        )}
      </Page>
    </FarmsContext.Provider>
  )
}

export const FarmsContext = createContext({ chosenFarmsMemoized: [], viewMode: ViewMode.TABLE, stakedOnlyState: false })
export default Farms
